import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { FaUserCog } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminFormLayout from './AdminFormLayout';
import { filterMenuModuleRecords } from './outletMenuModuleRegistry';

function AddEditKaryawanModules() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [roleName, setRoleName] = useState('');
    const [selectedModules, setSelectedModules] = useState([]);
    const [modules, setModules] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        fetchModules(token).then(() => {
            if (isEditMode) {
                fetchRoleDetails(token);
            }
        });
    }, [id, navigate, isEditMode]);

    /** ✅ Fetch Available Modules */
    const fetchModules = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });

            const data = await response.json();
            if (data.error === "0" && Array.isArray(data.results)) {
                setModules(filterMenuModuleRecords(data.results));
            } else {
                setModules([]);
            }
        } catch (err) {
            console.error('Failed to fetch modules:', err);
            setModules([]);
        }
    };

    /** ✅ Fetch Role Details (for Editing) */
    const fetchRoleDetails = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}get-default-karyawan-module`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ id: parseInt(id, 10) })
            });

            const data = await response.json();
            if (data.error === 0 && data.data) {
                setRoleName(data.data.role_name);
                setSelectedModules(data.data.modules || []);
            }
        } catch (err) {
            console.error('Failed to fetch role details:', err);
        }
    };

    /** ✅ Handle Module Selection */
    const handleModuleSelection = (moduleId) => {
        setSelectedModules(prev =>
            prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]
        );
    };

    /** ✅ Handle Save */
    const handleSave = async (e) => {
        e.preventDefault();

        if (!roleName.trim()) {
            Swal.fire('Error!', 'Role name is required.', 'error');
            return;
        }

        const token = localStorage.getItem('sessionToken');
        const requestBody = {
            role_name: roleName,
            modules: selectedModules
        };

        const url = isEditMode
            ? `${process.env.REACT_APP_API_BASE_URL}default-karyawan-module`
            : `${process.env.REACT_APP_API_BASE_URL}default-karyawan-module`;

        const method = isEditMode ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(isEditMode ? { id: parseInt(id, 10), ...requestBody } : requestBody)
            });

            const data = await response.json();
            if (data.error === 0) {
                Swal.fire('Success!', `Role ${isEditMode ? 'updated' : 'added'} successfully.`, 'success')
                    .then(() => navigate('/defaultmodulekaryawan'));
            } else {
                Swal.fire('Error!', 'Failed to save role.', 'error');
            }
        } catch (err) {
            console.error('Failed to save role:', err);
            Swal.fire('Error!', 'Something went wrong.', 'error');
        }
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                icon={FaUserCog}
                eyebrow="Access policy"
                title={isEditMode ? 'Update Employee Role Policy' : 'Create Employee Role Policy'}
                description="Tetapkan modul standar untuk suatu peran karyawan agar pengelolaan akses dilakukan secara konsisten."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Default Employee Access', to: '/defaultmodulekaryawan' },
                    { label: isEditMode ? 'Update Role Policy' : 'Create Role Policy' },
                ]}
                tipsTitle="Policy Notes"
                tips={[
                    'Gunakan nama peran yang jelas dan mudah dipahami.',
                    'Tetapkan hanya modul yang memang diperlukan oleh peran tersebut.',
                    'Kebijakan ini akan menjadi acuan akses standar bagi karyawan baru.',
                ]}
            >
                <Form onSubmit={handleSave}>
                    <div className="field-stack">
                        <Form.Label>Role name</Form.Label>
                        <Form.Control
                            type="text"
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value)}
                            placeholder="Enter role name"
                            required
                        />
                    </div>

                    <div className="field-stack">
                        <Form.Label>Modules</Form.Label>
                        <div className="check-grid">
                            {modules.length > 0 ? (
                                modules.map(module => (
                                    <Form.Check
                                        key={module.ID}
                                        type="checkbox"
                                        label={module.Modulename}
                                        checked={selectedModules.includes(module.ID)}
                                        onChange={() => handleModuleSelection(module.ID)}
                                    />
                                ))
                            ) : (
                                <p>No modules available</p>
                            )}
                        </div>
                    </div>

                    <div className="form-actions">
                        <Button variant="primary" type="submit">
                            {isEditMode ? 'Save Changes' : 'Save Policy'}
                        </Button>
                        <Button variant="secondary" onClick={() => navigate('/defaultmodulekaryawan')}>
                            Cancel
                        </Button>
                    </div>
                </Form>
            </AdminFormLayout>
        </div>
    );
}

export default AddEditKaryawanModules;
